export enum DestinationTypeEnum {
  Streams = 0,
  Facebook = 2,
  YouTube = 3,
  Vimeo = 4,
  LinkedIn = 5,
  Other = 6,
  Microsoft = 7,
  Zoom = 8,
  Studio = 10,
}

/**
 * A destination returned by GET /v3/greenroom/:meetingId/destination.
 *
 * Two shapes can come back in the same response array:
 *
 * 1. **Real DB destination** — has `id`, `name`, `type`, etc. User-managed via add/update/delete.
 *
 * 2. **Synthetic BLP / platform destination** — only `{ url, streamKey, selected }`.
 *    No `id`, `name`, or `type`.
 *
 *    This represents the Brandlive platform broadcast target (Mux/IVS RTMP). The
 *    greenroom-API builds it on the fly from credentials fetched live from events-api
 *    on every GET, so credential rotations propagate without any DB writes. Its
 *    `selected` flag mirrors `gr_meetings.use_blp_destination` (a single boolean).
 *
 *    The missing `id` is intentional and load-bearing: PUT /destination with no `id`
 *    only flips `use_blp_destination`; it does not attempt to UPDATE a row. The BLP
 *    destination is also not user-addable (no way to express "another platform
 *    destination" in the data model) — the `Add Destination` UI omits it.
 *
 *    Renderers should normalize a missing `type` to `DestinationTypeEnum.Streams`
 *    and resolve label/icon via `getDefaultDestinationOption(channel?.type)` so the
 *    chip reads as "Webinar+" / "BrandTV" — not "Studio" (the default option[0]).
 *
 * Source: dashboard-API `v3/greenroom/broadcast-utilities.js` → `getBlpDestination()`.
 */
export type Destination = {
  id?: number;
  name?: string;
  url: string;
  streamKey: string;
  logo?: string | null;
  type?: DestinationTypeEnum;
  selected: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type DestinationsResponse = {
  destinations: Destination[];
  resolution: string;
};

export type DestinationBody = {
  id?: number;
  name: string;
  url: string;
  streamKey: string;
  logo?: string;
  type: DestinationTypeEnum;
  selected: boolean;
};

export type CreateDestinationPayload = { destination: DestinationBody };
export type UpdateDestinationPayload = { destination: Partial<DestinationBody> };
